/**
 * photoQueue — coda offline per foto sopralluogo (iter v33 r56)
 * ════════════════════════════════════════════════════════════════════
 * User (iPhone prod): «ho scattato una foto e ha impiegato molto, ho
 * dovuto chiudere l'app, riaprire e riscattare».
 *
 * Root cause: su iOS Capacitor WKWebView, quando l'app va in background
 * durante l'upload di una foto (schermo bloccato, chiamata, home button),
 * iOS sospende la network. La request axios muore, e nel vecchio codice
 * il file era in RAM (React state) e veniva perso al reload della pagina.
 *
 * Fix: PRIMA di ogni tentativo di upload salviamo il blob in IndexedDB.
 * Se l'upload fallisce, il blob resta salvato. Al prossimo mount della
 * RecordingPhase (o su richiesta esplicita) rilanciamo l'upload delle
 * foto in coda. Zero perdita di dati.
 *
 * IndexedDB e' supportato nativamente da WKWebView (iOS Capacitor) e da
 * qualsiasi browser moderno — nessuna dipendenza da installare.
 *
 * Struttura record: {
 *   id: string (uuid),
 *   session_id: string (sopralluogo id),
 *   blob: Blob,
 *   filename: string,
 *   size: number,
 *   created_at: number (unix ms),
 *   retries: number,
 * }
 */

const DB_NAME = "pronto-photo-queue";
const DB_VERSION = 1;
const STORE = "pending";

let _dbPromise = null;

function _openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB non supportato"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("session_id", "session_id", { unique: false });
        store.createIndex("created_at", "created_at", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function _uuid() {
  // Semplice generatore uuid v4-ish (non serve crypto perche' e' locale)
  return "p_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

/**
 * Salva un blob nella coda locale (pre-upload).
 * @param {string} sessionId — id sessione sopralluogo
 * @param {Blob|File} blob — blob della foto
 * @param {string} filename — nome file suggerito
 * @returns {Promise<string>} id record
 */
export async function enqueuePhoto(sessionId, blob, filename = "photo.jpg") {
  try {
    const db = await _openDb();
    const id = _uuid();
    const record = {
      id,
      session_id: sessionId,
      blob,
      filename,
      size: blob?.size || 0,
      created_at: Date.now(),
      retries: 0,
    };
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(record);
      tx.oncomplete = () => resolve(id);
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    // Se IndexedDB fallisce, non blocchiamo l'upload — solo perdiamo la coda
    console.warn("[photoQueue] enqueue failed", e);
    return null;
  }
}

/**
 * Rimuove un record dopo upload riuscito.
 */
export async function dequeuePhoto(id) {
  if (!id) return;
  try {
    const db = await _openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[photoQueue] dequeue failed", e);
  }
}

/**
 * Elenca le foto pending per una specifica sessione.
 * @returns {Promise<Array<{id, session_id, blob, filename, size, created_at, retries}>>}
 */
export async function listPending(sessionId) {
  try {
    const db = await _openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const idx = tx.objectStore(STORE).index("session_id");
      const req = idx.getAll(sessionId);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn("[photoQueue] list failed", e);
    return [];
  }
}

/**
 * Incrementa il contatore retries su un record (per audit / max-retry logic).
 */
export async function incrementRetry(id) {
  if (!id) return;
  try {
    const db = await _openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const rec = getReq.result;
        if (rec) {
          rec.retries = (rec.retries || 0) + 1;
          store.put(rec);
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[photoQueue] increment failed", e);
  }
}

/**
 * Pulizia periodica: rimuove record piu' vecchi di 7 giorni per non
 * accumulare Mb di foto vecchie che l'utente ha probabilmente gia'
 * riscattato manualmente.
 */
export async function pruneOld(maxAgeMs = 7 * 24 * 3600 * 1000) {
  try {
    const db = await _openDb();
    const cutoff = Date.now() - maxAgeMs;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const idx = store.index("created_at");
      const range = IDBKeyRange.upperBound(cutoff);
      idx.openCursor(range).onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[photoQueue] prune failed", e);
  }
}
